/**
 * Orologio di palinsesto = Date.now() + offset (API.md, GET /api/v3/time).
 * Stima: 8 campioni, mediana degli offset dei 4 con RTT minore.
 * L'RTT si misura con un clock monotono (performance.now), l'istante di invio con Date.now():
 * così l'offset è coerente con Date.now(), che è ciò che useremo per correggerlo.
 */

export interface Sample {
  offset: number;
  rtt: number;
}

export interface TimeSource {
  wall(): number; // ms epoch (Date.now)
  mono(): number; // ms monotono (performance.now)
}

export const SYSTEM_TIME: TimeSource = {
  wall: () => Date.now(),
  mono: () => performance.now(),
};

export function median(values: number[]): number {
  if (values.length === 0) throw new Error('mediana di un insieme vuoto');
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? (s[mid] as number) : ((s[mid - 1] as number) + (s[mid] as number)) / 2;
}

/** Mediana degli offset dei `keep` campioni con RTT minore. */
export function estimateOffset(samples: Sample[], keep = 4): number {
  const best = [...samples].sort((a, b) => a.rtt - b.rtt).slice(0, keep);
  return Math.round(median(best.map((s) => s.offset)));
}

export class SyncedClock {
  private offsetMs = 0;
  private known = false;

  constructor(private readonly time: TimeSource = SYSTEM_TIME) {}

  /** Orologio di palinsesto (ms epoch). */
  now(): number {
    return this.time.wall() + this.offsetMs;
  }

  get offset(): number {
    return this.offsetMs;
  }

  get hasOffset(): boolean {
    return this.known;
  }

  setOffset(ms: number): void {
    this.offsetMs = ms;
    this.known = true;
  }

  /** Raccoglie i campioni (sequenziali) e aggiorna l'offset. Restituisce l'offset stimato. */
  async sync(fetchServerTimeMs: () => Promise<number>, samples = 8): Promise<number> {
    const got: Sample[] = [];
    let lastError: unknown = null;
    for (let i = 0; i < samples; i++) {
      const w0 = this.time.wall();
      const m0 = this.time.mono();
      try {
        const server = await fetchServerTimeMs();
        const rtt = this.time.mono() - m0;
        got.push({ offset: server - (w0 + rtt / 2), rtt });
      } catch (e) {
        lastError = e;
      }
    }
    if (got.length === 0) throw lastError instanceof Error ? lastError : new Error('nessun campione di orologio');
    const off = estimateOffset(got, Math.min(4, Math.ceil(got.length / 2)));
    this.setOffset(off);
    return off;
  }
}
